<template>
  <form
    autocomplete="off"
    class="settings-editing"
    v-on:submit.prevent="onSubmit"
  >
    <input
      hidden="hidden"
      type="submit">
    <v-dialog v-model="isShowLogo">
      <v-card class="show-image">
        <v-card-title>
          Логотип
          <v-spacer />
          <v-btn
            icon
            v-on:click="isShowLogo = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <div class="show-image__container">
          <img
            alt="Логотип"
            v-bind:src="logoSource" />
        </div>
      </v-card>
    </v-dialog>
    <div class="settings-editing__row-short">
      <input-text
        placeholder="Название клиники"
        title="Укажите название клиники"
        v-bind:violations="clinicNameInputErrors"
        v-letter-capital
        v-model="clinicName" />
      <input-file
        title="Логотип"
        v-bind:accept="$options.acceptedList"
        v-bind:violations="logoInputErrors"
        v-bind:with-view-button="true"
        v-model="logo"
        v-on:download-file="onDownloadFile"
        v-on:view-file="onViewLogo" />
    </div>
  </form>
</template>

<script>
import InputFile from '@/components/InputFile.vue';
import InputText from '@/components/InputText.vue';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.violationCauses ? vm.violationCauses[cause] : []
);

/**
 * @description Сохранить файл.
 * @param {any}    file Файл.
 * @param {string} name Название файла.
 */
function saveFile(file, name) {
  const obj = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = obj;
  link.target = '_blank';
  link.download = name.replace(/[.]/g, '-');
  link.click();
  URL.revokeObjectURL(link.href);
  link.remove();
}

/**
 * @description Форма заполнения настроек клиники.
 */
export default {
  acceptedList: [
    'image/png',
    'image/jpeg',
    '.jpeg',
    '.png',
  ].join(', '),
  components: {
    InputFile,
    InputText,
  },
  computed: {
    clinicNameInputErrors: resolveInputErrors('clinic_name'),
    isChanged() {
      return this.clinicName !== this.initialClinicName
          || this.logo !== this.initialLogo;
    },
    logoInputErrors: resolveInputErrors('logo'),
  },
  data() {
    return {
      /**
       * @description Логотип.
       * @type {}
       */
      logo: this.initialLogo,
      /**
       * @description Изображение логотипа.
       * @type {string}
       */
      logoSource: null,
      /**
       * @description Название клиники.
       * @type {string}
       */
      clinicName: this.initialClinicName,
      /**
       * @description Показать логотип.
       * @type {boolean}
       */
      isShowLogo: false,
    };
  },
  methods: {
    /**
     * @description Обработать просмотр логотипа.
     * @returns {void}
     */
    onViewLogo() {
      if (this.logo.url) {
        this.logoSource = this.logo.url;
      } else {
        this.logoSource = URL.createObjectURL(this.logo);
      }
      this.isShowLogo = true;
    },
    /**
     * @description Обработать событие загрузки файла.
     * @param {{url:string}|File} logo Логотип.
     * @returns {void}
     */
    onDownloadFile(logo) {
      if (logo.url) {
        const onResponse = (response) => {
          saveFile(response, this.initialClinicName);
        };
        this
          .$settingGateway
          .downloadLogo()
          .then((result) => result.forEach(onResponse));
      } else {
        saveFile(logo, logo.name);
      }
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      this.$emit('submit');
    },
    /**
     * @description Обработать отправку по триггеру.
     * @returns {void}
     */
    onSubmitTrigger() {
      const data = {
        clinicName: this.clinicName,
        logo: this.logo,
      };
      this.$emit('submit-by-trigger', this.submitTrigger, data);
    },
  },
  props: {
    initialClinicName: {
      default: () => '',
      type: String,
    },
    initialLogo: {
      default: () => null,
      type: Object,
    },
    /**
     * @description Триггер отправки.
     * @type {number}
     */
    submitTrigger: {
      required: true,
      type: Number,
    },
    /**
     * @description Нарушения ограничений.
     * @type {any}
     */
    violationCauses: {
      required: true,
      type: Object,
    },
  },
  watch: {
    isChanged() {
      this.$emit('change', this.isChanged);
    },
    submitTrigger(updatedValue) {
      if (updatedValue) {
        this.onSubmitTrigger();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.settings-editing {
  display: grid;
  grid-column-gap: 12px;
  grid-row-gap: 15px;
  grid-template-columns: repeat(3, 1fr);
  width: 740px;
  & &__egisz-block {
    display: flex;
    flex-direction: column;
    gap: 15px;
    grid-column-end: 4;
    grid-column-start: 1;
    margin: 10px 0 0 0;
  }
  & &__row,
  & &__row-short {
    align-items: baseline;
    display: grid;
    grid-column-end: 4;
    grid-column-start: 1;
    grid-gap: 12px;
  }
  & &__row {
    grid-template-columns: repeat(3, 1fr);
  }
  & &__row-short {
    grid-template-columns: repeat(2, 1fr);
  }
  & &__caption {
    font-size: 16px;
    font-weight: 600;
    grid-column-end: 3;
    grid-column-start: 1;
    grid-row-end: 3;
    grid-row-start: 2;
    line-height: 19px;
    margin: 20px 0 0 0;
  }
}

.show-image {
  display: flex;
  flex-direction: column;
  height: 90vh;
  & &__container {
    flex: 1;
    position: relative;
  }
  & &__container img {
    object-fit: contain;
    position: absolute;
    width: 100%;
    padding: 0 20px;
  }
}

@media screen and (max-width: 900px) {
  .settings-editing {
    width: 100%;
  }
}

@media screen and (max-width: 800px) {
  .settings-editing {
    grid-column-gap: 0;
    grid-template-columns: 1fr;
    & &__caption {
      grid-column-end: 2;
      grid-column-start: 1;
    }
    & &__row {
      grid-column-end: 2;
      grid-column-start: 1;
      grid-template-columns: 1fr;
    }
  }
}

@media screen and (max-width: 600px) {
  .settings-editing {
    & &__row-short {
      grid-template-columns: 1fr;
    }
  }
}
</style>
